/**
 * Iter64 — Histórico PLACE: importar ZIPs anuales + ver progreso/resumen.
 */
import React, { useEffect, useState } from 'react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Loader2, Database, RefreshCw, Download, AlertTriangle, Upload as UploadIcon, FileArchive } from 'lucide-react';
import { toast } from 'sonner';
import { req } from '../services/api';

const fmt = (n) => Number(n ?? 0).toLocaleString('es-ES');

const ESTADO_COLOR = {
  encolado: 'bg-ink-100 text-ink-700 border-ink-200',
  descargando: 'bg-blue-50 text-blue-700 border-blue-200',
  procesando: 'bg-amber-50 text-amber-700 border-amber-200',
  completado: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  error: 'bg-red-50 text-red-700 border-red-200',
};

export default function HistoricoPlace() {
  const currentYear = new Date().getFullYear();
  const [seleccion, setSeleccion] = useState({});
  const [resumen, setResumen] = useState(null);
  const [estado, setEstado] = useState([]);
  const [importando, setImportando] = useState(false);

  const años = Array.from({ length: currentYear - 2011 }, (_, i) => currentYear - i);

  const cargar = async () => {
    try {
      const [r, e] = await Promise.all([
        req('/api/prospeccion/historico/resumen'),
        req('/api/prospeccion/historico/estado'),
      ]);
      setResumen(r); setEstado(e);
    } catch (err) { toast.error(err.message); }
  };

  useEffect(() => {
    cargar();
    const t = setInterval(cargar, 8000);
    return () => clearInterval(t);
  }, []);

  const lanzar = async () => {
    const años_sel = Object.entries(seleccion).filter(([, v]) => v).map(([k]) => Number(k));
    if (años_sel.length === 0) { toast.error('Selecciona al menos un año'); return; }
    setImportando(true);
    try {
      const r = await req('/api/prospeccion/historico/importar', { method: 'POST', body: JSON.stringify({ años: años_sel }) });
      toast.success(`${r.jobs.length} ${r.jobs.length === 1 ? 'job encolado' : 'jobs encolados'}`);
      setSeleccion({});
      cargar();
    } catch (e) { toast.error(e.message); }
    finally { setImportando(false); }
  };

  return (
    <div className="space-y-6" data-testid="page-historico-place">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-heading font-bold text-3xl text-ink-900 flex items-center gap-2">
            <Database className="w-7 h-7 text-brand-500" /> Histórico PLACE
          </h1>
          <p className="text-sm text-ink-500 mt-1">
            Importa los ZIPs anuales de PLACE/Hacienda (CODICE 2.07) para rellenar el universo
            de licitaciones de agua de cada año. Cada ZIP puede tardar entre 5 y 20 minutos.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={cargar}>
          <RefreshCw className="w-4 h-4 mr-1.5" /> Actualizar
        </Button>
      </div>

      {/* Resumen agregado */}
      <Card className="p-5 border-ink-200 bg-white" data-testid="historico-resumen">
        <h2 className="font-heading font-semibold text-ink-800 mb-3">Resumen acumulado</h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Stat label="Total licitaciones" value={fmt(resumen?.total_licitaciones)} />
          <Stat label="Adjudicadas" value={fmt(resumen?.total_adjudicadas)} />
          <Stat label="Gestoras detectadas" value={fmt(resumen?.total_gestoras_detectadas)} />
          <Stat label="Municipios cubiertos" value={fmt(resumen?.cobertura_municipios)} />
          <Stat label="Años procesados" value={(resumen?.años_procesados || []).join(', ') || '—'} />
        </div>
      </Card>

      {/* Selector de años */}
      <Card className="p-5 border-ink-200 bg-white">
        <h2 className="font-heading font-semibold text-ink-800 mb-3">Seleccionar años a importar</h2>
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2 mb-4">
          {años.map((y) => (
            <label key={y} className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer transition ${
              seleccion[y] ? 'bg-brand-50 border-brand-300' : 'bg-white border-ink-200 hover:bg-ink-50'
            }`} data-testid={`historico-año-${y}`}>
              <input
                type="checkbox" className="accent-brand-500"
                checked={!!seleccion[y]}
                onChange={(e) => setSeleccion({ ...seleccion, [y]: e.target.checked })}
              />
              <span className="text-sm font-medium">{y}</span>
            </label>
          ))}
        </div>
        <div className="flex items-center justify-between">
          <p className="text-xs text-ink-400 flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5" />
            Los años ya importados se actualizarán (UPSERT por place_id) — no se duplican datos.
          </p>
          <Button
            className="bg-brand-500 hover:bg-brand-600 text-white"
            onClick={lanzar} disabled={importando}
            data-testid="btn-historico-lanzar"
          >
            {importando ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Download className="w-4 h-4 mr-1.5" />}
            Importar seleccionados
          </Button>
        </div>
      </Card>

      {/* Iter66 — Subida manual de ZIP (modo D) */}
      <UploadZipCard onUploaded={cargar} />

      {/* Iter67 — Portugal BASE.gov.pt */}
      <PortugalCard />

      {/* Progreso de jobs */}
      <Card className="p-5 border-ink-200 bg-white">
        <h2 className="font-heading font-semibold text-ink-800 mb-3">Estado de los jobs</h2>
        {estado.length === 0 ? (
          <p className="text-sm text-ink-400 py-4 text-center">No hay jobs ejecutados todavía.</p>
        ) : (
          <div className="space-y-2" data-testid="historico-jobs">
            {estado.map((j) => (
              <div key={j.fuente} className="flex items-center gap-3 px-3 py-2 rounded border border-ink-100" data-testid={`job-${j.fuente}`}>
                <Badge className={`${ESTADO_COLOR[j.estado] || 'bg-ink-100'} border min-w-[110px] text-center`}>{j.estado || '—'}</Badge>
                <span className="font-mono text-sm text-ink-700 min-w-[160px]">{j.fuente}</span>
                <span className="text-xs text-ink-500 flex-1">
                  {j.atoms_procesados != null && `${j.atoms_procesados}/${j.atoms_total} XMLs · `}
                  agua: <strong>{fmt(j.agua_encontrados)}</strong> · entries: {fmt(j.total_entries)}
                  {j.error && <span className="text-red-600 ml-2">{j.error}</span>}
                </span>
                <span className="text-[11px] text-ink-400">{j.ultima_importacion?.slice(0, 19).replace('T', ' ')}</span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div>
    <p className="text-xs text-ink-500">{label}</p>
    <p className="text-2xl font-heading font-bold text-ink-900">{value}</p>
  </div>
);

// Iter66 — Modo D: dropzone + selector de año para subir ZIP manualmente.
const UPLOAD_LIMIT_MB = 480;

function UploadZipCard({ onUploaded }) {
  const currentYear = new Date().getFullYear();
  const [file, setFile] = React.useState(null);
  const [año, setAño] = React.useState(currentYear);
  const [subiendo, setSubiendo] = React.useState(false);
  const [progreso, setProgreso] = React.useState(0);
  const [drag, setDrag] = React.useState(false);
  const inputRef = React.useRef(null);

  const sizeMB = file ? file.size / (1024 * 1024) : 0;
  const exceedsLimit = sizeMB > UPLOAD_LIMIT_MB;

  const handleFile = (f) => {
    if (!f) return;
    if (!/\.zip$/i.test(f.name)) {
      toast.error('El archivo debe ser un .zip');
      return;
    }
    setFile(f);
    setProgreso(0);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    handleFile(e.dataTransfer?.files?.[0]);
  };

  const subir = async () => {
    if (!file) { toast.error('Selecciona un ZIP'); return; }
    if (exceedsLimit) {
      toast.error(`ZIP supera el límite de ${UPLOAD_LIMIT_MB} MB. Usa el selector de años.`);
      return;
    }
    setSubiendo(true);
    setProgreso(0);
    try {
      const fd = new FormData();
      fd.append('año', String(año));
      fd.append('fichero', file);
      const token = localStorage.getItem('token');
      // Usamos XHR para tener barra de progreso (fetch no expone progress en upload)
      const r = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${process.env.REACT_APP_BACKEND_URL}/api/prospeccion/historico/upload-zip`);
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.upload.onprogress = (ev) => {
          if (ev.lengthComputable) setProgreso(Math.round((ev.loaded / ev.total) * 100));
        };
        xhr.onload = () => {
          let data = null;
          try { data = JSON.parse(xhr.responseText); } catch (_) { /* noop */ }
          if (xhr.status >= 200 && xhr.status < 300) resolve(data);
          else reject(new Error((data && data.detail) || `Error ${xhr.status}`));
        };
        xhr.onerror = () => reject(new Error('Error de red'));
        xhr.send(fd);
      });
      toast.success(`ZIP subido (${(r.bytes_recibidos / 1024 / 1024).toFixed(0)} MB) · job ${r.jobs[0].job_id.slice(0, 8)}…`);
      setFile(null);
      setProgreso(0);
      if (inputRef.current) inputRef.current.value = '';
      onUploaded && onUploaded();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSubiendo(false);
    }
  };

  return (
    <Card className="p-5 border-ink-200 bg-white" data-testid="historico-upload-card">
      <h2 className="font-heading font-semibold text-ink-800 mb-1 flex items-center gap-2">
        <UploadIcon className="w-5 h-5 text-brand-500" /> O sube el ZIP manualmente
      </h2>
      <p className="text-xs text-ink-500 mb-3">
        Útil para anuales históricos. Descárgalo desde
        <a href="https://contrataciondelsectorpublico.gob.es/sindicacion" target="_blank" rel="noreferrer" className="text-brand-600 hover:underline mx-1">
          contrataciondelsectorpublico.gob.es/sindicacion
        </a>
        y suelta el archivo aquí. Máximo {UPLOAD_LIMIT_MB} MB.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-3 items-stretch">
        <div
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          className={`rounded-md border-2 border-dashed cursor-pointer flex items-center justify-center text-sm transition px-4 py-3 ${
            drag ? 'border-brand-500 bg-brand-50' : 'border-ink-200 hover:border-brand-300 hover:bg-ink-50'
          }`}
          data-testid="historico-dropzone"
        >
          {file ? (
            <span className="flex items-center gap-2">
              <FileArchive className="w-4 h-4 text-brand-500" />
              <span className="font-mono text-xs text-ink-700">{file.name}</span>
              <span className="text-xs text-ink-400">({sizeMB.toFixed(1)} MB)</span>
            </span>
          ) : (
            <span className="text-ink-500">
              Arrastra el .zip o <span className="text-brand-600 underline">selecciona</span>
            </span>
          )}
          <input
            ref={inputRef}
            type="file"
            accept=".zip"
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
            data-testid="historico-file-input"
          />
        </div>
        <select
          className="h-10 rounded-md border border-ink-200 bg-white text-sm px-3"
          value={año}
          onChange={(e) => setAño(Number(e.target.value))}
          data-testid="historico-upload-año"
        >
          {Array.from({ length: currentYear - 2011 }, (_, i) => currentYear - i).map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
        <Button
          className="bg-brand-500 hover:bg-brand-600 text-white"
          onClick={subir}
          disabled={!file || exceedsLimit || subiendo}
          data-testid="btn-historico-upload"
        >
          {subiendo ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <UploadIcon className="w-4 h-4 mr-1.5" />}
          {subiendo ? `${progreso}%` : 'Subir ZIP'}
        </Button>
      </div>
      {exceedsLimit && (
        <p
          className="mt-2 text-sm text-red-600 flex items-center gap-1.5"
          data-testid="historico-upload-error-size"
        >
          <AlertTriangle className="w-4 h-4" />
          Este ZIP supera el límite de subida ({UPLOAD_LIMIT_MB} MB). Usa el selector de años para
          descargarlo directamente desde Hacienda.
        </p>
      )}
      {subiendo && (
        <div className="mt-3 h-2 rounded-full bg-ink-100 overflow-hidden" data-testid="historico-upload-progress">
          <div className="h-full bg-brand-500 transition-all" style={{ width: `${progreso}%` }} />
        </div>
      )}
    </Card>
  );
}


// Iter67 — Sección Portugal (API BASE.gov.pt, sin ZIPs).
function PortugalCard() {
  const [seleccion, setSeleccion] = React.useState({});
  const [estado, setEstado] = React.useState([]);
  const [importando, setImportando] = React.useState(false);

  const años = Array.from({ length: 16 }, (_, i) => 2025 - i); // 2025..2010

  const cargar = async () => {
    try { setEstado(await req('/api/prospeccion/portugal/estado')); }
    catch (_) { /* noop */ }
  };

  React.useEffect(() => {
    cargar();
    const t = setInterval(cargar, 8000);
    return () => clearInterval(t);
  }, []);

  const lanzar = async () => {
    const sel = Object.entries(seleccion).filter(([, v]) => v).map(([k]) => Number(k));
    if (sel.length === 0) { toast.error('Selecciona al menos un año'); return; }
    setImportando(true);
    try {
      const r = await req('/api/prospeccion/portugal/importar', {
        method: 'POST', body: JSON.stringify({ años: sel }),
      });
      toast.success(`${r.jobs.length} ${r.jobs.length === 1 ? 'job encolado' : 'jobs encolados'} para Portugal`);
      setSeleccion({});
      cargar();
    } catch (e) { toast.error(e.message); }
    finally { setImportando(false); }
  };

  return (
    <Card className="p-5 border-ink-200 bg-white" data-testid="historico-portugal-card">
      <h2 className="font-heading font-semibold text-ink-800 mb-1 flex items-center gap-2">
        <Database className="w-5 h-5 text-brand-500" />
        Portugal — BASE.gov.pt
      </h2>
      <p className="text-xs text-ink-500 mb-3">
        Importa contratos adjudicados de agua desde la API REST de BASE.gov.pt (CPV 65100000-65130000).
        Sin ZIPs, sin límites de tamaño — descarga directa por CPV y año.
      </p>
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2 mb-4">
        {años.map((y) => (
          <label
            key={y}
            className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer transition ${
              seleccion[y] ? 'bg-brand-50 border-brand-300' : 'bg-white border-ink-200 hover:bg-ink-50'
            }`}
            data-testid={`portugal-año-${y}`}
          >
            <input
              type="checkbox" className="accent-brand-500"
              checked={!!seleccion[y]}
              onChange={(e) => setSeleccion({ ...seleccion, [y]: e.target.checked })}
            />
            <span className="text-sm font-medium">{y}</span>
          </label>
        ))}
      </div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-ink-400 flex items-center gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5" />
          UPSERT por place_id=portbase_{'{idcontrato}'} — re-importar no duplica.
        </p>
        <Button
          className="bg-brand-500 hover:bg-brand-600 text-white"
          onClick={lanzar}
          disabled={importando}
          data-testid="btn-portugal-importar"
        >
          {importando ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Download className="w-4 h-4 mr-1.5" />}
          Importar Portugal
        </Button>
      </div>

      {estado.length > 0 && (
        <div className="border-t border-ink-100 pt-3" data-testid="portugal-estado">
          <p className="text-xs font-semibold text-ink-500 uppercase tracking-wider mb-2">Últimas ejecuciones</p>
          <div className="space-y-1.5">
            {estado.slice(0, 5).map((s) => (
              <div key={s.fuente} className="flex items-center gap-3 text-xs">
                <Badge className={`${
                  s.estado === 'completado' ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                  : s.estado === 'error' ? 'bg-red-50 text-red-700 border-red-200'
                  : 'bg-amber-50 text-amber-700 border-amber-200'
                } border min-w-[100px] text-center`}>{s.estado || '—'}</Badge>
                <span className="font-mono text-ink-700 min-w-[140px]">{s.fuente}</span>
                <span className="text-ink-500 flex-1">
                  anuncios: <strong>{s.anuncios_listados || 0}</strong>
                  {' · '}contratos: <strong>{s.contratos_obtenidos || 0}</strong>
                  {' · '}agua: <strong>{s.agua_encontrados || 0}</strong>
                  {' · '}gestoras nuevas: <strong>{s.gestoras_creadas || 0}</strong>
                </span>
                <span className="text-[11px] text-ink-400">{(s.ultima_importacion || '').slice(0, 19).replace('T', ' ')}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}
